// src/firebase/auth.service.js
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    updateProfile,
    signInWithPopup,
    fetchSignInMethodsForEmail,
    setPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
    sendPasswordResetEmail,
} from "firebase/auth";
import { auth, googleProvider } from "./firebase.config";

/**
 * Sign up a new user with email and password.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<UserCredential>}
 */
export const signup = (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
};

/**
 * Log in an existing user with email and password.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<UserCredential>}
 */
export const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
};

/**
 * Log out the current user.
 * @returns {Promise<void>}
 */
export const logout = () => {
    return signOut(auth);
};

/**
 * Update Firebase Auth profile.
 * @param {User} user
 * @param {Object} profileData
 * @returns {Promise<void>}
 */
export const updateAuthProfile = (user, profileData) => {
    return updateProfile(user, profileData);
};

/**
 * Sign in with Google using a popup.
 * @returns {Promise<UserCredential>}
 */
export const signInWithGoogle = () => {
    return signInWithPopup(auth, googleProvider);
};

/**
 * Fetch sign-in methods associated with an email.
 * @param {string} email
 * @returns {Promise<string[]>}
 */
export const getSignInMethodsForEmail = (email) => {
    return fetchSignInMethodsForEmail(auth, email);
};

/**
 * Set Firebase Auth persistence.
 * @param {boolean} keepSignedIn - true for LOCAL (survives browser close), false for SESSION (tab only)
 * @returns {Promise<void>}
 */
export const setAuthPersistence = (keepSignedIn) => {
    return setPersistence(
        auth,
        keepSignedIn ? browserLocalPersistence : browserSessionPersistence
    );
};

/**
 * Send a password reset email via Firebase Auth.
 * @param {string} email
 * @returns {Promise<void>}
 */
export const sendPasswordReset = (email) => {
    return sendPasswordResetEmail(auth, email);
};
